import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsElement from '../../core/ids-element';

import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { requestAnimationTimeout } from '../../utils/ids-timer-utils/ids-timer-utils';
import IdsSplitterLocalStorage from './ids-splitter-local-storage';
import IdsSplitterPane from './ids-splitter-pane';
import '../ids-draggable/ids-draggable';

import styles from './ids-splitter.scss';

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * Drag context interface - tracks transient state during split bar dragging
 */
interface DragContext {
  /** Initial start pane size (negative for calculation purposes) */
  startSize: number;
  /** Initial end pane size */
  endSize: number;
  /** Padding value */
  pad: number;
  /** Maximum size constraint (for two-pane splitters with max-size) */
  max?: number;
}

/**
 * Size configuration for each pane
 */
interface PaneSizeConfig {
  elem: IdsSplitterPane;
  idx: number;
  size: number;
  minSize: number;
}

/**
 * Accumulated size data during initialization
 */
interface SizeAccumulator {
  adjustableSizes: PaneSizeConfig[];
  adjustableMinSizes: Array<{ elem: IdsSplitterPane; idx: number; minSize: number }>;
  totalSizes: number;
  totalMinSizes: number;
}

/**
 * Pane info in a pair
 */
interface SplitterPaneInfo {
  /** The pane element */
  pane: IdsSplitterPane;
  /** The pane index */
  idx: number;
  /** The pane size percentage */
  size: number;
  /** The pane minimum size percentage */
  minSize: number;
  /** The pane id attribute */
  id: string;
}

/**
 * Pair of adjacent panes with their split bar
 */
interface SplitterPair {
  /** The split bar draggable element */
  splitBar: SplitBarElement;
  /** The start (left/top) pane info */
  start: SplitterPaneInfo;
  /** The end (right/bottom) pane info */
  end: SplitterPaneInfo;
  /** The pair index (0-based) */
  idx: number;
}

/**
 * Collapse expand options interface
 */
export interface IdsSplitterCollapseExpandOpts {
  /** The start pane element */
  startPane?: HTMLElement | string;
  /** The end pane element */
  endPane?: HTMLElement | string;
  /** The pair object */
  pair?: SplitterPair;
  /** The initial values object */
  initial?: any;
  /**
   * When true, fully collapses the start pane to 0% regardless of min-size
   */
  fullCollapse?: boolean;
}

/**
 * Extended HTMLElement interface for split bar elements with transform limit properties
 */
interface SplitBarElement extends HTMLElement {
  /** Minimum X transform limit in pixels */
  minTransformX?: string;
  /** Maximum X transform limit in pixels */
  maxTransformX?: string;
  /** Minimum Y transform limit in pixels */
  minTransformY?: string;
  /** Maximum Y transform limit in pixels */
  maxTransformY?: string;
  /** Whether the split bar is currently being dragged */
  isDragging?: boolean;
}

/** Valid transform limit property names */
type TransformLimitKey = 'minTransformX' | 'maxTransformX' | 'minTransformY' | 'maxTransformY';

// Defaults
const SPLITTER_DEFAULTS = {
  align: 'start',
  axis: 'x',
  disabled: false,
  disableAutoCollapse: false,
  label: 'Resize',
  resizeOnDragEnd: false,
  savePosition: false,
  uniqueId: null,
  showExpandButton: true,
  alwaysShowExpandButton: false,
  minWidth: 0,
  expandedWidth: 250,
};

// Axis to be used only
const AXIS = ['x', 'y'];

// Event name strings
const EVENTS = {
  beforecollapsed: 'beforecollapsed',
  collapsed: 'collapsed',
  beforeexpanded: 'beforeexpanded',
  expanded: 'expanded',
  beforesizechanged: 'beforesizechanged',
  sizechanged: 'sizechanged'
};

// Collapsed strings
const COLLAPSED = 'collapsed';

/**
 * IDS Splitter Component
 * @type {IdsSplitter}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @part splitter - the splitter container element
 * @part split-bar - the split bar element
 * @part split-bar-icon - the split bar icon element
 */
@customElement('ids-splitter')
@scss(styles)
export default class IdsSplitter extends Base {
  constructor() {
    super();
    this.state = {
      ...this.state,
      ...SPLITTER_DEFAULTS
    };
  }

  #isRefreshing = false;

  /**
   * Check if currently refreshing sizes (prevents infinite recursion)
   * @returns {boolean} true if currently refreshing
   */
  get isRefreshing(): boolean {
    return this.#isRefreshing;
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.ALWAYS_SHOW_EXPAND_BUTTON,
      attributes.AXIS,
      attributes.DISABLE_AUTO_COLLAPSE,
      attributes.DISABLED,
      attributes.EXPANDED_WIDTH,
      attributes.HEIGHT,
      attributes.LABEL,
      attributes.MIN_DISTANCE,
      attributes.MIN_WIDTH,
      attributes.RESIZE_ON_DRAG_END,
      attributes.SAVE_POSITION,
      attributes.SHOW_EXPAND_BUTTON,
      attributes.TEXT_WRAP,
      attributes.UNIQUE_ID,
    ];
  }

  /**
   * React to attributes changing on the web-component
   * @param {string} name The property name
   * @param {string} oldValue The property old value
   * @param {string} newValue The property new value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;

    if (name === attributes.HEIGHT) this.#height(newValue);
  }

  /**
   * Get user-set height for splitter
   * @returns {string} the user-set height
   */
  get height(): string { return this.getAttribute(attributes.HEIGHT) || '100%'; }

  /**
   * Override the splitter's height
   * @param {string} value the user-set height
   */
  set height(value: string) { this.setAttribute(attributes.HEIGHT, value || '100%'); }

  /**
   * Helper for when height attribute changes
   * @param {string} value override splitter height
   * @see IdsSplitter.attributeChangedCallback
   */
  #height(value: string) {
    if (value) {
      this.container?.style.setProperty('height', value);
    } else {
      this.container?.style.removeProperty('height');
    }
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const cssClass = ` class="ids-splitter axis-${this.axis}"`;
    const disabled = this.disabled ? ' disabled' : '';
    return `
      <div part="splitter" role="presentation"${cssClass}${disabled}>
        <slot></slot>
      </div>`;
  }

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  /**
   * Get list of current pane sizes.
   * @returns {Array} The list of current pane sizes.
   */
  sizes(): Array<any> {
    return this.splitterPanes.map((pane) => pane.getSizeAsNumber());
  }

  /**
   * Get list of current pane minimum sizes.
   * @returns {Array} The list of current pane minimum sizes.
   */
  minSizes(): Array<any> {
    return this.splitterPanes.map((pane) => pane.state.computedMinSize || pane.getMinSizeAsNumber());
  }

  /**
   * Get list of current pane maximum sizes, limited to single split.
   * @returns {Array} The list of current pane maximum sizes.
   */
  maxSizes(): Array<any> {
    return this.splitterPanes
      .map((pane) => pane.state.computedMaxSize)
      .filter((maxSize) => maxSize > 0);
  }

  /**
   * Collapse start pane size for given start/end panes or panes css selector.
   * @param {object} [options] The collapse options.
   * @param {HTMLElement|string} [options.startPane] The start pane.
   * @param {HTMLElement|string} [options.endPane] The end pane.
   * @param {object} [options.pair] The pair.
   * @returns {void}
   */
  collapse(options: IdsSplitterCollapseExpandOpts = {}): void {
    if (this.disabled) {
      if (!options.initial) return;
    }
    const pair = options.pair || this.getPair(options);
    if (pair && pair.start) {
      let canProceed = true;
      const response = (veto: boolean) => {
        canProceed = !!stringToBool(veto);
      };
      this.#triggerEvent(EVENTS.beforecollapsed, pair, response);
      if (!canProceed) {
        return;
      }
      const { pane, idx } = pair.start;
      const before = this.#getPaneSize(idx);

      // For fullCollapse, ignore the default COLLAPSED_THRESHOLD_PX floor and
      // the splitter's min-distance (which is a drag-interaction constraint).
      // Only respect the pane's own explicit min-size attribute.
      let targetMinSize: number;
      if (options.fullCollapse) {
        targetMinSize = pane?.getMinSizeAsNumber?.() ?? 0;
      } else {
        targetMinSize = this.getPaneMinSize(idx);
      }
      const hasSize = this.#getPaneSize(idx) > targetMinSize;

      // Save current size before collapsing.
      // Only overwrite the existing expand size when this is a fresh user-driven
      // collapse. With `initial: true` (e.g. during resize/reapplication of
      // collapsed state), preserve any previously saved expand size so the
      // pane can still expand back to its original width.
      if (hasSize && !options.initial) {
        pane.saveExpandSize(this.#getPaneSize(idx));
      } else if (hasSize && options.initial && !pane.getExpandSize()) {
        pane.saveExpandSize(this.#getPaneSize(idx));
      }

      if (hasSize) {
        const diff = targetMinSize - this.#getPaneSize(idx);
        const pixelDiff = this.#toPixel(diff);
        // For fullCollapse override the pair's start minSize so #adjustDiff
        // does not clamp the move at the default collapse threshold.
        const movePair = options.fullCollapse
          ? { ...pair, start: { ...pair.start, minSize: targetMinSize } }
          : pair;
        this.#move({
          ...movePair, diff: pixelDiff, initial: options.initial, fullCollapse: options.fullCollapse
        });
      }

      if (before !== this.#getPaneSize(idx)) {
        if (!this.disableAutoCollapse) pane.setAttribute(COLLAPSED, '');
        this.#positionSplitBars();
        this.#triggerEvent(EVENTS.collapsed, pair);
      }
    }
  }

  /**
   * Expand start pane size for given start/end panes or panes css selector.
   * @param {object} [options] The expand options.
   * @param {HTMLElement|string} [options.startPane] The start pane.
   * @param {HTMLElement|string} [options.endPane] The end pane.
   * @param {object} [options.pair] The pair.
   * @returns {void}
   */
  expand(options: IdsSplitterCollapseExpandOpts = {}): void {
    if (this.disabled) {
      return;
    }
    const pair = options.pair || this.getPair(options);
    if (pair && pair.start) {
      let canProceed = true;
      const response = (veto: boolean) => {
        canProceed = !!stringToBool(veto);
      };
      this.#triggerEvent(EVENTS.beforeexpanded, pair, response);
      if (!canProceed) {
        return;
      }
      const { pane, idx } = pair.start;
      const before = this.#getPaneSize(idx);
      const collapsed = stringToBool(pane.getAttribute(COLLAPSED));
      if (collapsed) {
        const targetSize = pane.getExpandSize() || this.#defaultsSize;
        const diff = targetSize - this.#getPaneSize(idx);
        this.#move({ ...pair, diff: this.#toPixel(diff) });
      }

      if (before !== this.#getPaneSize(idx)) {
        pane.removeAttribute(COLLAPSED);
        this.#positionSplitBars();
        this.#triggerEvent(EVENTS.expanded, pair);
      }
    }
  }

  /**
   * Get list of all pairs.
   * @returns {SplitterPair[]} The array of pairs.
   */
  getAllPairs(): SplitterPair[] {
    return this.#pairs;
  }

  /**
   * Get pair for given start/end panes or panes css selector.
   * @param {object} [options] The expand options.
   * @param {HTMLElement|string} [options.startPane] The start pane.
   * @param {HTMLElement|string} [options.endPane] The end pane.
   * @returns {SplitterPair|undefined} The pair.
   */
  getPair(options: IdsSplitterCollapseExpandOpts = {}): SplitterPair | undefined {
    const { startPane, endPane } = options;
    let pair: SplitterPair | undefined;
    const isValid = (el: any) => typeof el === 'string' || el instanceof IdsSplitterPane;
    if (isValid(startPane) && isValid(endPane)) {
      const elem = (sel: any) => (typeof sel === 'string' ? this.querySelector(sel) : sel);
      const startElem = elem(startPane);
      const endElem = elem(endPane);
      this.#pairs.forEach((p) => {
        if (p.start.pane === startElem && p.end.pane === endElem) {
          pair = p;
        } else if (p.start.pane === endElem && p.end.pane === startElem) {
          pair = {
            ...p, start: p.end, end: p.start
          };
        }
      });
    }
    return pair;
  }

  /**
   * Container width.
   * @private
   */
  #container: any = { size: 0 };

  /**
   * Store some properties based on current axis.
   * @private
   */
  #prop: any = {};

  /**
   * List of pair elements attached.
   * @private
   */
  #pairs: SplitterPair[] = [];

  /**
   * Active drag contexts by split bar
   * @private
   */
  #dragContexts = new WeakMap<HTMLElement, DragContext>();

  /**
   * Defaults pane size.
   * @private
   */
  #defaultsSize = 0;

  /**
   * Attach the resize observer.
   * @private
   */
  #resizeObserver = new ResizeObserver(() => this.resize());

  /**
   * Observe the parent element for size changes caused by sibling visibility changes.
   * @private
   */
  #parentResizeObserver = new ResizeObserver(() => this.resize());

  /**
   * Observe the previous sibling (e.g. ids-header) for size changes.
   * Detects when a sibling is hidden/shown so the splitter can recalculate its height.
   * @private
   */
  #siblingResizeObserver = new ResizeObserver(() => this.resize());

  /**
   * Observe attribute/style mutations on the previous sibling to detect display changes.
   * ResizeObserver cannot detect display:none, so a MutationObserver is needed.
   * @private
   */
  #siblingMutationObserver: MutationObserver | null = null;

  #isTogglingExpand = false; // Flag to prevent dragend from overwriting expand sizes during programmatic changes

  #resizePending = false; // Debounce flag to prevent duplicate resize calls

  initialized: boolean = false;

  /**
   * Initialize or re-initialize the splitter component.
   *
   * This method orchestrates the complete initialization sequence in the correct order:
   * 1. Cleanup: Remove previous state and event handlers
   * 2. Configure: Set axis-based properties (horizontal/vertical)
   * 3. Measure: Calculate container dimensions
   * 4. Calculate: Compute pane sizes from attributes or defaults
   * 5. Render: Create split bar DOM elements
   * 6. Structure: Build internal pairs data structure
   * 7. Layout: Position split bars based on pane sizes
   * 8. Events: Attach drag, keyboard, and resize handlers
   * 9. Finalize: Apply initial collapsed state to panes
   * @private
   */
  #initObserverCallback() {
    this.initialized = false;
    this.#destroy();
    this.#setProp();
    this.#setContainer();
    this.#updateSizes();
    this.#addSplitBars();
    this.#setPairs();
    this.#positionSplitBars();
    this.#attachEventHandlers();
    this.#setInitialCollapsed();
    this.#setDisabled(); // Apply disabled state to split bars if set before initialization
    this.initialized = true;
  }

  /**
   * Get the minimum size for a pane, considering both the configured min-size and collapsed threshold
   * @param {number} paneIndex The index of the pane to get minimum size for
   * @returns {number} The minimum size in percentage, ensuring it's at least the collapsed threshold
   */
  getPaneMinSize(paneIndex: number) {
    const minCollapsedSize = this.#toPercentage(IdsSplitter.COLLAPSED_THRESHOLD_PX);
    const pane = this.splitterPanes[paneIndex];
    const paneMinSize = pane ? (pane.state.computedMinSize || pane.getMinSizeAsNumber()) : 0;

    // If the pane explicitly sets min-size="0", allow collapsing fully to 0
    // instead of enforcing the default COLLAPSED_THRESHOLD_PX (40px) floor
    const hasExplicitZeroMin = pane?.getAttribute(attributes.MIN_SIZE) === '0';
    if (hasExplicitZeroMin) return 0;

    const targetSize = Math.max(paneMinSize, minCollapsedSize);
    return targetSize;
  }

  /**
   * Get pane size by index from DOM (replaces array access)
   * @private
   * @param {number} idx The pane index
   * @returns {number} The pane size as percentage
   */
  #getPaneSize(idx: number): number {
    const pane = this.splitterPanes[idx];
    return pane ? pane.getSizeAsNumber() : 0;
  }

  /**
   * Set pane size by index to DOM and pane state
   * @private
   * @param {number} idx The pane index
   * @param {number} size The size as percentage
   */
  #setPaneSize(idx: number, size: number): void {
    const pane = this.splitterPanes[idx];
    if (pane) {
      // If the pane is pixel-based, use stored pixel value directly to avoid
      // precision loss from px → % → px round-trip conversion
      if (pane.isPixelBased()) {
        const pixelValue = pane.state.pixelSizeValue || this.#toPixel(size);
        pane.style[this.#prop.dimension] = `${pixelValue}px`;
        const wasRefreshing = this.#isRefreshing;
        this.#isRefreshing = true;
        pane.setAttribute(attributes.SIZE, `${pixelValue}px`);
        this.#isRefreshing = wasRefreshing;
        pane.state.pixelSizeValue = pixelValue;
      } else {
        pane.style[this.#prop.dimension] = `${this.#round(size)}%`;
        const wasRefreshing = this.#isRefreshing;
        this.#isRefreshing = true;
        pane.setAttribute(attributes.SIZE, `${this.#round(size)}%`);
        this.#isRefreshing = wasRefreshing;
      }
      pane.state.currentSize = size;
    }
  }

  /**
   * Refresh the sizes - sync CSS from attributes and reposition split bars
   * Called when a pane's size attribute is changed programmatically
   * @param {IdsSplitterPane} [triggeringPane] The pane that triggered this refresh
   * @private
   */
  refreshSizes(triggeringPane?: any) {
    if (!this.initialized || this.#isRefreshing) return;

    const panes = this.splitterPanes;
    if (panes.length === 0) return;

    this.#isRefreshing = true;

    try {
      // If a specific pane triggered this, adjust other panes to maintain 100% total
      if (triggeringPane) {
        // Detect if the triggering pane's new size is pixel-based
        const triggeringSizeAttr = triggeringPane.getAttribute(attributes.SIZE) || '0';
        const isPixelValue = /px$/i.test(triggeringSizeAttr) || /^\d+(\.\d+)?$/.test(triggeringSizeAttr.trim());

        if (isPixelValue) {
          triggeringPane.state.isPixelSize = true;
          triggeringPane.state.pixelSizeValue = parseFloat(triggeringSizeAttr);

          // Auto-switch: only one pane can be pixel-based at a time.
          // Convert any other pixel-based sibling panes to their current percentage equivalent.
          const otherPixelPanes = panes.filter(
            (p) => p !== triggeringPane && p.isPixelBased()
          );
          otherPixelPanes.forEach((pane) => {
            const currentPercent = pane.state.currentSize || this.#percentageVal(pane.getAttribute(attributes.SIZE) || '0');
            pane.state.isPixelSize = false;
            pane.state.pixelSizeValue = 0;
            pane.style[this.#prop.dimension] = `${this.#round(currentPercent)}%`;
            const wasRefreshing = this.#isRefreshing;
            this.#isRefreshing = true;
            pane.setAttribute(attributes.SIZE, `${this.#round(currentPercent)}%`);
            this.#isRefreshing = wasRefreshing;
            pane.state.currentSize = currentPercent;
          });
        } else {
          // Reset pixel state when switching back to percentage
          triggeringPane.state.isPixelSize = false;
          triggeringPane.state.pixelSizeValue = 0;
        }

        let triggeringSize = this.#percentageVal(triggeringSizeAttr);

        // Enforce max-size constraint on the triggering pane
        const maxSizeAttr = triggeringPane.getAttribute(attributes.MAX_SIZE);
        if (maxSizeAttr) {
          const maxSize = parseFloat(maxSizeAttr);
          if (!Number.isNaN(maxSize) && triggeringSize > maxSize) {
            triggeringSize = maxSize;
          }
        }

        // Enforce min-size constraint on the triggering pane
        const minSizeAttr = triggeringPane.getAttribute(attributes.MIN_SIZE);
        if (minSizeAttr) {
          const minSize = parseFloat(minSizeAttr);
          if (!Number.isNaN(minSize) && triggeringSize < minSize) {
            triggeringSize = minSize;
          }
        }

        const otherPanes = panes.filter((p) => p !== triggeringPane);
        const remainingSize = 100 - triggeringSize;

        if (otherPanes.length > 0) {
          // Calculate current total of other panes
          let otherTotal = 0;
          otherPanes.forEach((pane) => {
            otherTotal += this.#percentageVal(pane.getAttribute(attributes.SIZE) || '0');
          });

          // Distribute remaining size proportionally among other panes
          otherPanes.forEach((pane) => {
            const currentSize = this.#percentageVal(pane.getAttribute(attributes.SIZE) || '0');
            const newSize = otherTotal > 0 ? (currentSize / otherTotal) * remainingSize : remainingSize / otherPanes.length;
            if (pane.isPixelBased()) {
              const newPixelValue = this.#toPixel(newSize);
              pane.style[this.#prop.dimension] = `${newPixelValue}px`;
              pane.setAttribute(attributes.SIZE, `${newPixelValue}px`);
              pane.state.pixelSizeValue = newPixelValue;
            } else {
              pane.style[this.#prop.dimension] = `${this.#round(newSize)}%`;
              pane.setAttribute(attributes.SIZE, `${this.#round(newSize)}%`);
            }
            pane.state.currentSize = newSize;
          });
        }

        // Apply triggering pane's size preserving its unit
        if (triggeringPane.isPixelBased()) {
          // Use the original pixel value directly to avoid precision loss
          // from px → % → px round-trip conversion
          const originalPixelValue = triggeringPane.state.pixelSizeValue;
          triggeringPane.style[this.#prop.dimension] = `${originalPixelValue}px`;
          triggeringPane.setAttribute(attributes.SIZE, `${originalPixelValue}px`);
        } else {
          triggeringPane.style[this.#prop.dimension] = `${this.#round(triggeringSize)}%`;
          triggeringPane.setAttribute(attributes.SIZE, `${this.#round(triggeringSize)}%`);
        }
        triggeringPane.state.currentSize = triggeringSize;
      } else {
        // Just sync CSS from attributes, preserving original units
        panes.forEach((pane) => {
          const sizeAttr = pane.getAttribute(attributes.SIZE);
          if (sizeAttr) {
            if (pane.isPixelBased()) {
              // Use stored pixel value directly to avoid precision loss
              const pixelValue = pane.state.pixelSizeValue || parseFloat(sizeAttr);
              pane.style[this.#prop.dimension] = `${pixelValue}px`;
              pane.state.currentSize = this.#percentageVal(sizeAttr);
            } else {
              const sizePercent = this.#percentageVal(sizeAttr);
              pane.style[this.#prop.dimension] = `${this.#round(sizePercent)}%`;
              pane.state.currentSize = sizePercent;
            }
          }
        });
      }

      // Sync pair sizes so drag calculations use current values
      this.#pairs.forEach((pair) => {
        pair.start.size = this.#getPaneSize(pair.start.idx);
        pair.end.size = this.#getPaneSize(pair.end.idx);
      });

      // Reposition split bars based on current sizes
      this.#positionSplitBars();
    } finally {
      this.#isRefreshing = false;
    }
  }

  /**
   * Attach the initialize observer.
   * @private
   */
  #initObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.#initObserver?.disconnect();
          this.#initObserverCallback();
        }
      });
    }) : null;

  /**
   * Local storage instance attached to component.
   * @private
   */
  #ls = new IdsSplitterLocalStorage(this);

  /**
   * Clear the saved position from local storage
   * @param {string} uniqueId If undefined, will use Internal attached.
   * @returns {void}
   */
  clearPosition = this.#ls.clearPosition.bind(this.#ls);

  /**
   * Clear all related saved position from local storage
   * @returns {void}
   */
  clearPositionAll = this.#ls.clearPositionAll.bind(this.#ls);

  /**
   * Get the id to be use.
   * @param {string} uniqueId The uniqueId.
   * @param {string} suffix Optional suffix string to make the id more unique.
   * @param {string} prefix Optional prefix string to make the id more unique.
   * @returns {string} The id.
   */
  idTobeUsed = this.#ls.idTobeUsed.bind(this.#ls);

  /**
   * Initialize the component
   * @private
   * @returns {void}
   */
  #init() {
    if (this.#initObserver && this.container) this.#initObserver.observe(this.container);
    else this.#initObserverCallback();
  }

  /**
   * Resize the component
   * @returns {void}
   */
  resize(): void {
    if (this.#resizePending) return;
    this.#resizePending = true;
    requestAnimationFrame(() => {
      this.#resizePending = false;
      this.#adjustHeightForHeader();
      this.#setContainer();
      this.#setProp();

      // On resize, pixel-based panes keep their fixed pixel size.
      // Recalculate their percentage equivalent and redistribute
      // remaining space among percentage-based panes.
      const panes = this.splitterPanes;
      if (panes.length > 0 && this.#container.size > 0) {
        const pixelPanes: Array<{ pane: any; idx: number; pxSize: number }> = [];
        const percentPanes: Array<{ pane: any; idx: number; currentPercent: number }> = [];

        panes.forEach((pane, idx) => {
          if (pane.isPixelBased() && pane.getPixelSizeValue() > 0) {
            pixelPanes.push({ pane, idx, pxSize: pane.getPixelSizeValue() });
          } else {
            percentPanes.push({ pane, idx, currentPercent: pane.state.currentSize || 0 });
          }
        });

        if (pixelPanes.length > 0 && percentPanes.length > 0) {
          // Calculate total percentage consumed by pixel panes at new container size
          let pixelPercentTotal = 0;
          pixelPanes.forEach((item) => {
            const newPercent = (item.pxSize / this.#container.size) * 100;
            pixelPercentTotal += newPercent;
            this.#isRefreshing = true;
            item.pane.style[this.#prop.dimension] = `${item.pxSize}px`;
            item.pane.state.currentSize = newPercent;
            this.#isRefreshing = false;
          });

          // Redistribute remaining percentage among non-pixel panes
          const remainingPercent = Math.max(0, 100 - pixelPercentTotal);
          let currentPercentTotal = 0;
          percentPanes.forEach((item) => { currentPercentTotal += item.currentPercent; });

          percentPanes.forEach((item) => {
            const newPercent = currentPercentTotal > 0
              ? (item.currentPercent / currentPercentTotal) * remainingPercent
              : remainingPercent / percentPanes.length;
            this.#setPaneSize(item.idx, newPercent);
          });

          // Update pixel pane internal percentage for positioning calculations
          pixelPanes.forEach((item) => {
            const newPercent = (item.pxSize / this.#container.size) * 100;
            item.pane.state.currentSize = newPercent;
          });
        }
      }

      this.#positionSplitBars();
      this.#setInitialCollapsed();
    });
  }

  /**
   * Public method to force the splitter to recalculate its dimensions.
   * Useful when the layout around the splitter changes (e.g. siblings hidden/shown)
   * and the ResizeObserver alone cannot detect the change.
   *
   * This is a convenience alias for `resize()`. Prefer `refresh()` in application code
   * when responding to external layout changes. The `resize()` method is used internally
   * by observers and may be subject to debouncing or guard logic in the future.
   * @returns {void}
   */
  refresh(): void {
    this.resize();
  }

  /**
   * Set to store properties based on current axis.
   * @private
   * @returns {object} This API object for chaining
   */
  #setProp(): object {
    let prop = {};
    if (this.axis === 'y') {
      prop = {
        orientation: 'vertical',
        dimension: 'height',
        posStart: 'top',
        posEnd: 'bottom',
        delta: 'dragDeltaY',
        translate: 'translateY',
        minTransform: 'minTransformY',
        maxTransform: 'maxTransformY',
        useRTL: false
      };
    } else {
      prop = {
        orientation: 'horizontal',
        dimension: 'width',
        posStart: 'left',
        posEnd: 'right',
        delta: 'dragDeltaX',
        translate: 'translateX',
        minTransform: 'minTransformX',
        maxTransform: 'maxTransformX',
        useRTL: this.localeAPI?.isRTL()
      };
    }
    this.#prop = { ...prop, barPixel: 0, barPercentage: this.#toPercentage(0) };
    return this;
  }

  /**
   * Set container size width/height value based on current axis.
   * @private
   * @returns {object} This API object for chaining
   */
  #setContainer(): object {
    if (!this.#container) return this;

    this.#container.rect = this.container?.getBoundingClientRect() || {};
    this.#container.posStart = this.#container.rect[this.#prop.posStart];
    this.#container.size = this.#container.rect[this.#prop.dimension];
    return this;
  }

  get splitterPanes() {
    // Use :scope > to select only direct children, not nested splitter panes
    return [...this.querySelectorAll(':scope > ids-splitter-pane')] as Array<any>;
  }

  /**
   * Parse and extract pane size attributes
   * @private
   * @param {IdsSplitterPane} pane The pane element
   * @returns {object} Parsed size configuration
   */
  #parsePaneSizes(pane: IdsSplitterPane): {
    size: number | null;
    minSize: number | null;
  } {
    const sizeAttr = pane.getAttribute('size');
    const minSizeAttr = pane.getAttribute('min-size');

    // Detect and track pixel-based sizes
    if (sizeAttr !== null) {
      const isPixel = /px$/i.test(sizeAttr) || /^\d+(\.\d+)?$/.test(sizeAttr.trim());
      if (isPixel) {
        pane.state.isPixelSize = true;
        pane.state.pixelSizeValue = parseFloat(sizeAttr);
      }
    }

    return {
      size: sizeAttr !== null ? this.#percentageVal(sizeAttr) : null,
      minSize: minSizeAttr !== null ? this.#percentageVal(minSizeAttr) : null
    };
  }

  /**
   * Collect initial size configuration from all panes
   * @private
   * @param {IdsSplitterPane[]} panes Array of pane elements
   * @returns {SizeAccumulator} Accumulated size data
   */
  #collectPaneSizes(panes: IdsSplitterPane[]): SizeAccumulator {
    const accumulator: SizeAccumulator = {
      adjustableSizes: [],
      adjustableMinSizes: [],
      totalSizes: 0,
      totalMinSizes: 0
    };

    panes.forEach((pane, idx) => {
      const { size, minSize } = this.#parsePaneSizes(pane);

      if (size !== null && minSize !== null) {
        // Both size and minSize specified - use the larger value
        const actualSize = Math.max(size, minSize);
        accumulator.totalSizes += actualSize;
        accumulator.totalMinSizes += minSize;
        accumulator.adjustableSizes.push({
          elem: pane,
          idx,
          size: actualSize,
          minSize
        });
        accumulator.adjustableMinSizes.push({ elem: pane, idx, minSize });
      } else if (size !== null) {
        // Only size specified
        accumulator.totalSizes += size;
        accumulator.adjustableSizes.push({
          elem: pane,
          idx,
          size,
          minSize: 0
        });
      } else if (minSize !== null) {
        // Only minSize specified
        accumulator.totalMinSizes += minSize;
        accumulator.adjustableMinSizes.push({ elem: pane, idx, minSize });
      }
    });

    // Auto-switch: only one pane can be pixel-based at a time.
    // If multiple panes have pixel sizes, keep the last one as pixel
    // and convert earlier ones to their percentage equivalent.
    const pixelPanes = panes.filter((p) => p.isPixelBased());
    if (pixelPanes.length > 1) {
      // Keep the last pixel pane, convert the rest to percentage
      const panesToConvert = pixelPanes.slice(0, -1);
      panesToConvert.forEach((pane) => {
        const currentPercent = pane.state.currentSize
          || this.#percentageVal(pane.getAttribute(attributes.SIZE) || '0');
        pane.state.isPixelSize = false;
        pane.state.pixelSizeValue = 0;
        pane.setAttribute(attributes.SIZE, `${this.#round(currentPercent)}%`);
        pane.state.currentSize = currentPercent;
      });
    }

    return accumulator;
  }

  /**
   * Reduce excess sizes proportionally when total exceeds 100%
   * @private
   * @param {Array<{size: number; idx: number}>} sizes Array of sizes to reduce
   * @param {number} excess Amount to reduce
   */
  #reduceExcessSizes(sizes: Array<{ size: number; idx: number }>, excess: number): void {
    // Sort by size descending to reduce from largest first
    const sorted = [...sizes].sort((a, b) => b.size - a.size);
    let remaining = excess;

    for (const item of sorted) {
      if (remaining <= 0) break;

      if (item.size >= remaining) {
        item.size -= remaining;
        remaining = 0;
      } else {
        remaining -= item.size;
        item.size = 0;
      }
    }

    // Re-sort by original index
    sizes.sort((a, b) => a.idx - b.idx);
  }

  /**
   * Handle max-size constraint for two-pane splitters
   * @private
   * @param {IdsSplitterPane[]} panes Array of panes
   * @param {SizeAccumulator} accumulator Size accumulator to modify
   */
  #applyMaxSizeConstraint(panes: IdsSplitterPane[], accumulator: SizeAccumulator): void {
    if (panes.length !== 2) return;

    const maxSizeAttr = panes[0].getAttribute('max-size');
    if (!maxSizeAttr) return;

    let maxSize = Math.min(this.#percentageVal(maxSizeAttr), 100);
    const hasSize = accumulator.adjustableSizes.length > 0 && accumulator.adjustableSizes[0].idx === 0;
    const hasMinSize = accumulator.adjustableMinSizes.length > 0 && accumulator.adjustableMinSizes[0].idx === 0;

    // Adjust maxSize if minSize is larger
    if (hasMinSize && accumulator.adjustableMinSizes[0].minSize >= maxSize) {
      maxSize = accumulator.adjustableMinSizes[0].minSize;
    }

    if (hasSize && !hasMinSize) {
      // Case 1: Has size but no minSize
      if (accumulator.adjustableSizes[0].size >= maxSize) {
        accumulator.totalSizes -= (accumulator.adjustableSizes[0].size - maxSize);
        accumulator.adjustableSizes[0].size = maxSize;
      }
    } else if (!hasSize && hasMinSize) {
      // Case 2: Has minSize but no size
      if (accumulator.adjustableMinSizes[0].minSize >= maxSize) {
        accumulator.totalSizes += maxSize;
        accumulator.adjustableSizes.push({
          elem: panes[0],
          idx: 0,
          size: maxSize,
          minSize: maxSize
        });
      }
    } else if (hasSize && hasMinSize) {
      // Case 3: Has both size and minSize
      if (accumulator.adjustableSizes[0].size >= maxSize) {
        accumulator.totalSizes -= (accumulator.adjustableSizes[0].size - maxSize);
        accumulator.adjustableSizes[0].size = maxSize;
      }
    } else {
      // Case 4: No size or minSize specified
      accumulator.totalSizes += maxSize;
      accumulator.adjustableSizes.push({
        elem: panes[0],
        idx: 0,
        size: maxSize,
        minSize: 0
      });
    }

    // Store computed max-size in first pane's state
    panes[0].state.computedMaxSize = maxSize;
  }

  /**
   * Adjust splitter height to account for adjacent header
   * @private
   */
  #adjustHeightForHeader(): void {
    const prev = this.previousElementSibling;
    if (prev && ['ids-header', 'ids-theme-switcher'].includes(prev?.tagName.toLowerCase())) {
      const height = prev.getBoundingClientRect().height;
      if (height > 0) {
        this.height = `calc(100% - ${height + 4}px)`;
      } else {
        this.height = '100%';
      }
    }
  }

  /**
   * Calculate default size for panes without explicit sizes
   * @private
   * @param {number} totalPanes Total number of panes
   * @param {number} usedPercentage Already allocated percentage
   * @param {number} panesWithSizes Number of panes with explicit sizes
   * @returns {number} Default size percentage
   */
  #calculateDefaultPaneSize(totalPanes: number, usedPercentage: number, panesWithSizes: number): number {
    const panesWithoutSize = totalPanes - panesWithSizes;
    if (panesWithoutSize <= 0) return 100 / totalPanes;

    const remainingPercentage = Math.max(0, 100 - usedPercentage);
    return remainingPercentage / panesWithoutSize;
  }

  /**
   * Apply size constraints and normalize to 100%
   * @private
   * @param {SizeAccumulator} accumulator Size accumulator to normalize
   * @param {number} totalPanes Total number of panes
   * @returns {number} Default pane size
   */
  #normalizeSizes(accumulator: SizeAccumulator, totalPanes: number): number {
    const TOTAL = 100;

    // Reduce excess minSizes if they exceed 100%
    if (accumulator.totalMinSizes >= TOTAL) {
      const excess = accumulator.totalMinSizes - TOTAL;
      this.#reduceExcessSizes(
        accumulator.adjustableMinSizes.map((item) => ({ size: item.minSize, idx: item.idx })),
        excess
      );
      accumulator.totalMinSizes = TOTAL;
    }

    // Reduce excess sizes if they exceed 100%
    if (accumulator.totalSizes >= TOTAL) {
      const excess = accumulator.totalSizes - TOTAL;
      this.#reduceExcessSizes(
        accumulator.adjustableSizes.map((item) => ({ size: item.size, idx: item.idx })),
        excess
      );
      accumulator.totalSizes = TOTAL;
    }

    // Calculate default size for panes without explicit sizes
    const defaultSize = this.#calculateDefaultPaneSize(
      totalPanes,
      accumulator.totalSizes,
      accumulator.adjustableSizes.length
    );

    // Ensure minSizes don't exceed calculated sizes or defaults
    accumulator.adjustableMinSizes.forEach((minSizeItem) => {
      const sizeItem = accumulator.adjustableSizes.find((s) => s.idx === minSizeItem.idx);
      const actualSize = sizeItem?.size ?? defaultSize;

      if (minSizeItem.minSize > actualSize) {
        if (sizeItem) {
          sizeItem.size = minSizeItem.minSize;
        } else {
          accumulator.adjustableSizes.push({
            elem: minSizeItem.elem,
            idx: minSizeItem.idx,
            size: minSizeItem.minSize,
            minSize: minSizeItem.minSize
          });
          accumulator.totalSizes += minSizeItem.minSize;
        }
      }
    });

    // Recalculate default if needed after minSize adjustments
    return this.#calculateDefaultPaneSize(
      totalPanes,
      accumulator.totalSizes,
      accumulator.adjustableSizes.length
    );
  }

  /**
   * Restore sizes from localStorage if available
   * @private
   * @param {IdsSplitterPane[]} panes Array of panes
   * @param {SizeAccumulator} accumulator Size accumulator to modify
   */
  #restoreSavedSizes(panes: IdsSplitterPane[], accumulator: SizeAccumulator): void {
    const saved = this.#ls.restorePosition();
    if (!saved?.sizes || saved.sizes.length !== panes.length) return;

    accumulator.adjustableSizes = panes.map((elem, idx) => ({
      elem,
      idx,
      size: saved.sizes?.[idx] ?? 0,
      minSize: accumulator.adjustableMinSizes.find((m) => m.idx === idx)?.minSize ?? 0
    }));
  }

  /**
   * Apply calculated sizes to DOM elements
   * @private
   * @param {IdsSplitterPane[]} panes Array of panes
   * @param {SizeAccumulator} accumulator Size data
   * @param {number} defaultSize Default size for panes without explicit sizes
   */
  #applySizesToDOM(panes: IdsSplitterPane[], accumulator: SizeAccumulator, defaultSize: number): void {
    panes.forEach((pane, idx) => {
      const sizeConfig = accumulator.adjustableSizes.find((s) => s.idx === idx);
      const minSizeConfig = accumulator.adjustableMinSizes.find((m) => m.idx === idx);

      const size = sizeConfig?.size ?? defaultSize;
      const minSize = minSizeConfig?.minSize ?? 0;

      // Apply to DOM - #setPaneSize handles px vs % based on pane.isPixelBased()
      this.#setPaneSize(idx, size);

      // Store pane computed min size in state
      pane.state.computedMinSize = minSize;
    });

    this.#defaultsSize = defaultSize;
    // Initialize expandSizes from current sizes - use actual pane size, not default
    this.splitterPanes.forEach((pane) => {
      if (!pane.getExpandSize()) {
        const currentSize = pane.getSizeAsNumber() || defaultSize;
        pane.saveExpandSize(currentSize);
      }
    });
  }

  /**
   * Set initial sizes, min-sizes and pane elements (REFACTORED)
   * @private
   * @returns {object} This API object for chaining
   */
  #updateSizes(): object {
    try {
      this.initialized = false;
      const panes = this.splitterPanes;

      if (panes.length === 0) {
        return this;
      }

      // Step 1: Collect size configurations from panes
      const accumulator = this.#collectPaneSizes(panes);

      // Step 2: Apply max-size constraint (two-pane only)
      this.#applyMaxSizeConstraint(panes, accumulator);

      // Step 3: Adjust height for adjacent header
      this.#adjustHeightForHeader();

      // Step 4: Normalize sizes and calculate defaults
      const defaultSize = this.#normalizeSizes(accumulator, panes.length);

      // Step 5: Restore from localStorage if available
      this.#restoreSavedSizes(panes, accumulator);

      // Step 6: Apply calculated sizes to DOM
      this.#applySizesToDOM(panes, accumulator, defaultSize);

      this.initialized = true;
      this.classList.add('ids-splitter-initialized');

      return this;
    } catch (error) {
      console.error('IdsSplitter: Error updating sizes', error);
      this.initialized = false;
      return this;
    }
  }

  /**
   * Get splitId prefix with given value, use for each split bar element.
   * @private
   * @param {number} value The value to be use as id.
   * @returns {string} prefix with given value.
   */
  #splitId(value: number): string {
    return `split-${value}`;
  }

  /**
   * Add split bar elements.
   * @private
   * @returns {object} This API object for chaining.
   */
  #addSplitBars(): object {
    const cssClass = ` class="splitter-dragger"`;
    const disabled = this.disabled ? ' disabled aria-disabled="true"' : '';
    const panes = this.splitterPanes;
    panes.forEach((pane, i) => {
      if (i > 0) {
        const template = document.createElement('template');
        template.innerHTML = `
          <ids-draggable axis=${this.axis} id="${this.#splitId(i)}"
            aria-orientation="${this.#prop.orientation}"
            role="separator" aria-label="${this.label}" tabindex="0"${cssClass}${disabled}>
            <div class="split-bar" part="split-bar" role="presentation">
              <div class="splitter-drag-handle"></div>
            </div>
          </ids-draggable>`;
        this.container?.appendChild(template.content.cloneNode(true));

        // Add expand button if enabled
        if (this.showExpandButton) {
          this.#addExpandButton(i);
        }
      }
    });
    return this;
  }

  /**
   * Adjust the position of the handle
   * @private
   * @param {CustomEvent} e the event from the handler
   */
  #moveDragHandle(e: MouseEvent) {
    const dragHandles = this.shadowRoot?.querySelectorAll<HTMLDivElement>('.splitter-drag-handle');
    dragHandles?.forEach((dragHandle: HTMLDivElement) => {
      const rect = this.container?.getBoundingClientRect() || {
        left: 0, top: 0, width: 0, height: 0
      };
      if (!dragHandle) return;

      const size = 32;
      const pad = size / 2;
      let start;
      let end;
      let position;

      // Vertical
      if (this.axis === 'y') {
        const clientX = e.clientX;
        start = clientX - rect.left - pad;
        if (start < pad) start = 0;
        end = rect.width - size;
        position = start > end ? end : start;
        dragHandle.style.left = `${position}px`;
        return;
      }

      // Horizontal
      const clientY = e.clientY;
      start = clientY - rect.top - pad;
      if (start < pad) start = 0;
      end = rect.height - size;
      position = start > end ? end : start;
      dragHandle.style.top = `${position}px`;
    });
  }

  /**
   * Each pair has reference to start/end panes and each split bar.
   * @private
   * @returns {object} This API object for chaining.
   */
  #setPairs(): object {
    this.shadowRoot?.host.setAttribute('role', 'group');
    const panes = this.splitterPanes;
    panes.forEach((pane, i) => {
      if (i > 0) {
        const splitBar = this.container?.querySelector<HTMLElement>(`#${this.#splitId(i)}`);
        if (!splitBar) return;

        const zIdx = i - 1;
        const start: SplitterPaneInfo = {
          pane: panes[zIdx],
          idx: zIdx,
          size: this.#getPaneSize(zIdx),
          minSize: this.getPaneMinSize(zIdx),
          id: ''
        };
        const end: SplitterPaneInfo = {
          pane,
          idx: i,
          size: this.#getPaneSize(i),
          minSize: this.getPaneMinSize(i),
          id: ''
        };
        start.id = start.pane.getAttribute('id') || '';
        if (!start.id) {
          start.id = this.#uniquePaneId();
          start.pane.setAttribute('id', start.id);
        }
        end.id = end.pane.getAttribute('id') || '';
        if (!end.id) {
          end.id = this.#uniquePaneId();
          end.pane.setAttribute('id', end.id);
        }
        // splitBar.setAttribute('aria-controls', `${start.id} ${end.id}`);

        this.#pairs.push({
          splitBar, start, end, idx: zIdx
        });
      }
    });
    return this;
  }

  /**
   * Calculate split bar spacing based on pane index
   * @private
   * @param {number} paneIndex The pane index
   * @returns {object} Min and max spacing in pixels
   */
  #calculateBarSpacing(paneIndex: number): { min: number; max: number } {
    return {
      min: paneIndex > 1 ? this.#prop.barPixel : 0,
      max: this.#prop.barPixel
    };
  }

  /**
   * Calculate transform limits for a split bar
   * @private
   * @param {object} params Calculation parameters
   * @param {number} params.startPosition The start position in pixels
   * @param {number} params.endPosition The end position in pixels
   * @param {any} params.pair The pair object
   * @param {object} params.barSpacing The bar spacing object
   * @param {number} params.barSpacing.min Minimum spacing
   * @param {number} params.barSpacing.max Maximum spacing
   * @returns {object} Min and max transform values in pixels
   */
  #calculateTransformLimits(params: {
    startPosition: number;
    endPosition: number;
    pair: any;
    barSpacing: { min: number; max: number };
  }): { min: number; max: number } {
    const {
      startPosition, endPosition, pair, barSpacing
    } = params;

    const { start, end } = pair;
    const isFirstPane = start.idx === 0;
    const isLastPane = end.idx === this.splitterPanes.length - 1;

    // Calculate base minimum transform (left/top limit)
    // When the start pane has actual size 0 (fully collapsed), allow the bar
    // to sit at startPosition + 0 (no min-size floor) so it remains grabbable.
    const startIsZero = this.#getPaneSize(start.idx) === 0;
    const startMinPercent = startIsZero ? 0 : this.getPaneMinSize(start.idx);
    let minTransform = startPosition
      + this.#toPixel(startMinPercent)
      + barSpacing.min;

    // Apply edge constraint for first pane
    if (isFirstPane && !startIsZero) {
      minTransform = Math.max(minTransform, this.minDistance);
    }

    // Calculate base maximum transform (right/bottom limit)
    // When the end pane has actual size 0 (fully collapsed), allow the bar
    // to sit at endPosition - 0 so it remains grabbable.
    const endIsZero = this.#getPaneSize(end.idx) === 0;
    const endMinPercent = endIsZero ? 0 : this.getPaneMinSize(end.idx);
    let maxTransform = endPosition
      - this.#toPixel(endMinPercent)
      - barSpacing.max;

    // Apply edge constraint for last pane
    if (isLastPane && !endIsZero) {
      maxTransform = Math.min(maxTransform, this.#container.size - this.minDistance);
    }

    // Apply max-size constraint on start pane (limits how far right/down the bar can go)
    const startPane = this.splitterPanes[start.idx];
    const startMaxSize = startPane?.getMaxSizeAsNumber?.() ?? startPane?.state.computedMaxSize;
    if (startMaxSize && startMaxSize > 0) {
      const maxSizeLimit = startPosition + this.#toPixel(startMaxSize);
      maxTransform = Math.min(maxTransform, maxSizeLimit);
    }

    // Apply max-size constraint on end pane (limits how far left/up the bar can go)
    const endPane = this.splitterPanes[end.idx];
    const endMaxSize = endPane?.getMaxSizeAsNumber?.() ?? endPane?.state.computedMaxSize;
    if (endMaxSize && endMaxSize > 0) {
      const minSizeLimit = endPosition - this.#toPixel(endMaxSize);
      minTransform = Math.max(minTransform, minSizeLimit);
    }

    return { min: minTransform, max: maxTransform };
  }

  /**
   * Check if splitter has nested or mixed axis splitters
   * @private
   * @returns {boolean} True if nested splitters detected
   */
  #hasNestedOrMixedSplitters(): boolean {
    const rootElement = this.getRootNode() as Document | ShadowRoot;
    const allSplitters = rootElement.querySelectorAll('ids-splitter');
    let hasX = false;
    let hasY = false;

    for (const splitter of allSplitters) {
      const axis = splitter.getAttribute('axis');
      if (axis === 'y') hasY = true;
      else hasX = true;
      if (hasX && hasY) return true;
    }

    return false;
  }

  /**
   * Calculate split bar position considering nested splitters
   * @private
   * @param {number} position The base position
   * @returns {number} Adjusted position
   */
  #calculateSplitBarPosition(position: number): number {
    if (this.#hasNestedOrMixedSplitters()) {
      return Math.max(
        Math.min(position, this.#container.size - this.minDistance),
        this.minDistance
      );
    }
    return position;
  }

  /**
   * Apply RTL transform to split bar
   * @private
   * @param {SplitBarElement} splitBar The split bar element
   * @param {number} position The position in pixels
   * @param {object} limits The transform limits
   * @param {number} limits.min Minimum transform value
   * @param {number} limits.max Maximum transform value
   */
  #applySplitBarTransformRTL(
    splitBar: SplitBarElement,
    position: number,
    limits: { min: number; max: number }
  ): void {
    const { translate, minTransform, maxTransform } = this.#prop;
    splitBar.style.setProperty('transform', `${translate}(${position * -1}px)`);
    splitBar[minTransform as TransformLimitKey] = `${limits.max * -1}px`;
    splitBar[maxTransform as TransformLimitKey] = `${limits.min * -1}px`;
  }

  /**
   * Apply LTR transform to split bar
   * @private
   * @param {SplitBarElement} splitBar The split bar element
   * @param {number} position The position in pixels
   * @param {object} limits The transform limits
   * @param {number} limits.min Minimum transform value
   * @param {number} limits.max Maximum transform value
   */
  #applySplitBarTransformLTR(
    splitBar: SplitBarElement,
    position: number,
    limits: { min: number; max: number }
  ): void {
    const { translate, minTransform, maxTransform } = this.#prop;
    const adjustedPosition = this.#calculateSplitBarPosition(position);
    splitBar.style.setProperty('transform', `${translate}(${adjustedPosition}px)`);
    splitBar[minTransform as TransformLimitKey] = `${limits.min}px`;
    splitBar[maxTransform as TransformLimitKey] = `${limits.max}px`;
  }

  /**
   * Position a single split bar
   * @private
   * @param {object} params Positioning parameters
   * @param {any} params.pair The pair object containing start/end panes and split bar
   * @param {number} params.startPosition The start position in pixels
   * @param {number} params.midPosition The middle position in pixels
   * @param {number} params.endPosition The end position in pixels
   * @param {number} params.paneIndex The pane index
   */
  #positionSingleSplitBar(params: {
    pair: any;
    startPosition: number;
    midPosition: number;
    endPosition: number;
    paneIndex: number;
  }): void {
    const {
      pair, startPosition, midPosition, endPosition, paneIndex
    } = params;

    if (!pair) return;

    const { splitBar } = pair;
    if (!splitBar) return;

    const barSpacing = this.#calculateBarSpacing(paneIndex);
    const limits = this.#calculateTransformLimits({
      startPosition,
      endPosition,
      pair,
      barSpacing
    });

    if (this.#prop.useRTL) {
      this.#applySplitBarTransformRTL(splitBar, midPosition, limits);
    } else {
      this.#applySplitBarTransformLTR(splitBar, midPosition, limits);
    }
  }

  /**
   * Adjust split bars position (REFACTORED)
   * @private
   * @returns {object} This API object for chaining
   */
  #positionSplitBars(): object {
    const positions = { min: 0, mid: 0, max: 0 };

    this.splitterPanes.forEach((pane, i) => {
      const paneSize = this.#toPixel(this.#getPaneSize(i));
      positions.min = positions.mid;
      positions.mid = positions.max;
      positions.max += paneSize;

      if (i > 0) {
        const pair = this.#pairs[i - 1];
        this.#positionSingleSplitBar({
          pair,
          startPosition: positions.min,
          midPosition: positions.mid,
          endPosition: positions.max,
          paneIndex: i
        });
      }
    });

    return this;
  }

  /**
   * Set initial collapsed panes.
   * @private
   * @returns {object} This API object for chaining
   */
  #setInitialCollapsed(): object {
    this.#pairs.forEach((pair, index) => {
      const collapsed = stringToBool(pair.start.pane.getAttribute(COLLAPSED));
      const collapsedEnd = stringToBool(pair.end.pane.getAttribute(COLLAPSED));

      if (collapsed) {
        this.collapse({ pair, initial: true, fullCollapse: true });
      } else if (collapsedEnd) {
        this.collapse({ pair: { ...pair, start: pair.end, end: pair.start }, initial: true, fullCollapse: true });
      }
      // Update expand button state for all pairs
      this.#updateExpandButtonState(index + 1, collapsed || collapsedEnd);
    });
    return this;
  }

  /**
   * Set collapsed attribute to given start pane based start pane size.
   * @private
   * @param {any} pair The pair object.
   * @returns {object} This API object for chaining
   */
  #setCollapsedAttribute(pair: any): object {
    const { start, end, initial } = pair;
    window.requestAnimationFrame(() => {
      const bar = start.idx === 0 ? 0 : this.#prop.barPercentage;
      const currentSize = this.#getPaneSize(start.idx);
      const minSize = this.getPaneMinSize(start.idx) + bar;
      const isCurrentlyCollapsed = start.pane.hasAttribute(COLLAPSED);

      if (Math.round(currentSize) > Math.round(minSize)) {
        start.pane.removeAttribute(COLLAPSED);
        // Update expand button state when expanding
        this.#updateExpandButtonState(pair.idx + 1, false);
      } else if (!isCurrentlyCollapsed || initial) {
        // Save expand size only if one doesn't exist yet
        // This prevents overwriting the size saved during dragend
        const existingExpandSize = start.pane.getExpandSize();
        if (!existingExpandSize || existingExpandSize === 0) {
          // No expand size saved yet, save one
          if (!isCurrentlyCollapsed && pair.start?.size && pair.start.size > minSize) {
            // Save the size from before the drag started
            start.pane.saveExpandSize(pair.start.size);
          } else if (currentSize > minSize) {
            // Fallback: save current size if it's above min
            start.pane.saveExpandSize(currentSize);
          } else {
            // Last resort: save default size
            start.pane.saveExpandSize(this.#defaultsSize);
          }
        }
        // If expand size already exists, keep it (it was set by dragend handler)
        if (!this.disableAutoCollapse) start.pane.setAttribute(COLLAPSED, '');
        // Update expand button state when collapsing
        this.#updateExpandButtonState(pair.idx + 1, true);
      }

      // Also check the end pane for collapsed state
      if (end) {
        const endCurrentSize = this.#getPaneSize(end.idx);
        const endMinSize = this.getPaneMinSize(end.idx) + (this.#prop.barPercentage ?? 0);
        const isEndCollapsed = end.pane.hasAttribute(COLLAPSED);

        if (Math.round(endCurrentSize) > Math.round(endMinSize)) {
          // End pane is expanded, remove collapsed attribute
          end.pane.removeAttribute(COLLAPSED);
        } else if (!isEndCollapsed || initial) {
          // End pane is collapsed, set collapsed attribute
          const existingEndExpandSize = end.pane.getExpandSize();
          if (!existingEndExpandSize || existingEndExpandSize === 0) {
            if (!isEndCollapsed && pair.end?.size && pair.end.size > endMinSize) {
              end.pane.saveExpandSize(pair.end.size);
            } else if (endCurrentSize > endMinSize) {
              end.pane.saveExpandSize(endCurrentSize);
            } else {
              end.pane.saveExpandSize(this.#defaultsSize);
            }
          }
          if (!this.disableAutoCollapse) end.pane.setAttribute(COLLAPSED, '');
        }
      }
    });
    return this;
  }

  /**
   * Set disabled state to container and for each split bars.
   * @private
   * @returns {object} This API object for chaining
   */
  #setDisabled(): object {
    const toggleAttribute = (el: HTMLElement, attr = attributes.DISABLED, val = '') => {
      if (this.disabled) el.setAttribute(attr, val);
      else el.removeAttribute(attr);
    };
    if (this.container) toggleAttribute(this.container);
    this.#pairs.forEach((pair) => {
      toggleAttribute(pair.splitBar);
      toggleAttribute(pair.splitBar, 'aria-disabled', 'true');
    });
    return this;
  }

  /**
   * Create drag context for split bar movement
   * @private
   * @param {any} pair The pair object
   * @returns {DragContext | null} Drag context or null if vetoed
   */
  #createDragContext(pair: any): DragContext | null {
    let canProceed = true;
    const response = (veto: boolean) => {
      canProceed = !!stringToBool(veto);
    };
    this.#triggerEvent(EVENTS.beforesizechanged, pair, response);
    if (!canProceed) {
      pair.splitBar.isDragging = false;
      return null;
    }

    const { start, end } = pair;
    start.size = this.#getPaneSize(start.idx);
    end.size = this.#getPaneSize(end.idx);
    // Refresh min sizes too — they are percentages relative to container size,
    // so they go stale when the splitter container is resized (e.g. an outer
    // sibling layout collapses or expands). For an explicit full-collapse move,
    // preserve the caller's start min-size override so the default collapse
    // threshold is not re-applied and the pane can reach 0%.
    if (!pair.fullCollapse) start.minSize = this.getPaneMinSize(start.idx);
    end.minSize = this.getPaneMinSize(end.idx);

    // This object captures a snapshot of both panes' sizes at drag start
    // and defines the legal range of movement for the split bar.
    // Since diff is positive when growing the start pane,
    // a negative startSize means "you can shrink the start pane by at most this much."
    // The #adjustDiff method then clamps the actual drag delta within [startSize + min, endSize - min]
    // to prevent dragging past the start pane's minimum.
    const context: DragContext = {
      startSize: start.size * -1,
      endSize: end.size,
      pad: 1
    };

    // Apply max-size constraint if set on start pane
    const startPane = this.splitterPanes[start.idx];
    const startMaxSize = startPane?.getMaxSizeAsNumber?.() ?? startPane?.state.computedMaxSize;
    if (startMaxSize && startMaxSize > 0) {
      context.max = startMaxSize - this.#getPaneSize(start.idx) + this.getPaneMinSize(end.idx);
    }

    return context;
  }

  /**
   * Update a split bar's transform limits at drag start based on
   * current adjacent bar positions and max-size constraints.
   * @private
   * @param {number} pairIdx The index of this pair in #pairs
   * @param {SplitBarElement} splitBar The split bar element
   * @param {any} pair The pair object
   */
  #updateDragLimits(pairIdx: number, splitBar: SplitBarElement, pair: any): void {
    const { minTransform, maxTransform } = this.#prop;
    const minKey = minTransform as TransformLimitKey;
    const maxKey = maxTransform as TransformLimitKey;
    let min = parseFloat(splitBar[minKey] || '') || 0;
    let max = parseFloat(splitBar[maxKey] || '') || this.#container.size;

    // Constrain by adjacent bar positions so bars can't cross each other
    if (pairIdx > 0) {
      const prevBar = this.#pairs[pairIdx - 1].splitBar;
      const prevPos = parseFloat(prevBar?.style.transform?.match(/-?\d+(\.\d+)?/)?.[0] || '0');
      min = Math.max(min, prevPos + this.#prop.barPixel);
    }
    if (pairIdx < this.#pairs.length - 1) {
      const nextBar = this.#pairs[pairIdx + 1].splitBar;
      const nextPos = parseFloat(nextBar?.style.transform?.match(/-?\d+(\.\d+)?/)?.[0] || '0');
      max = Math.min(max, nextPos - this.#prop.barPixel);
    }

    // Constrain by start pane max-size
    const startPane = this.splitterPanes[pair.start.idx];
    const startMaxSize = startPane?.getMaxSizeAsNumber?.() ?? 0;
    if (startMaxSize > 0) {
      let startOrigin = 0;
      for (let j = 0; j < pair.start.idx; j++) {
        startOrigin += this.#toPixel(this.#getPaneSize(j));
      }
      max = Math.min(max, startOrigin + this.#toPixel(startMaxSize));
    }

    // Constrain by end pane max-size
    const endPane = this.splitterPanes[pair.end.idx];
    const endMaxSize = endPane?.getMaxSizeAsNumber?.() ?? 0;
    if (endMaxSize > 0) {
      let endOrigin = 0;
      for (let j = 0; j <= pair.end.idx; j++) {
        endOrigin += this.#toPixel(this.#getPaneSize(j));
      }
      min = Math.max(min, endOrigin - this.#toPixel(endMaxSize));
    }

    splitBar[minKey] = `${min}px`;
    splitBar[maxKey] = `${max}px`;
  }

  /**
   * Complete drag operation and update state
   * @private
   * @param {any} pair The pair object
   */
  #completeDrag(pair: any): void {
    this.#updateSizeAndSplitBar(pair);
    this.#setCollapsedAttribute(pair);

    // Recalculate all split bar positions from actual pane sizes
    // to prevent accumulated drift from incremental adjustments
    this.#positionSplitBars();

    this.#triggerEvent(EVENTS.sizechanged, pair);
    this.#ls.savePosition();
  }

  /**
   * Move split bar and adjust panes for given difference.
   * @private
   * @param {object} pair The pair object.
   * @returns {void}
   */
  #move(pair: any): void {
    const dragContext = this.#createDragContext(pair);
    if (!dragContext) return;

    // At this point, diff is already in logical percentage space (+ = grow start pane)
    // No RTL correction needed - that was handled when converting from physical pixels
    const diff = this.#adjustDiff({ ...pair, diff: this.#toPercentage(pair.diff) }, dragContext);
    this.#completeDrag({ ...pair, diff });

    if (diff) {
      const sb = pair.splitBar;

      let position = 0;
      for (let i = 0; i <= pair.start.idx; i++) {
        position += this.#toPixel(this.#getPaneSize(i));
      }
      sb?.style.setProperty('transform', `${this.#prop.translate}(${position}px)`);
    }
  }

  /**
   * Adjust given difference and ignore out of bound value.
   * @private
   * @param {object} pair The start object in pair
   * @param {DragContext} dragContext The drag context with size constraints
   * @returns {number} The adjusted difference
   */
  #adjustDiff(pair: any, dragContext: DragContext): number {
    const {
      startSize, endSize, pad, max
    } = dragContext;

    const { barPercentage } = this.#prop;
    const { start, end, diff } = pair;
    const useEndSize = typeof max !== 'undefined' ? max : endSize;

    const extra: any = {};
    extra.min = start.idx === 0 ? 0 : barPercentage;
    extra.max = barPercentage;

    // Apply minDistance for multiple split bars OR nested splitters
    const hasMultipleSplitBars = this.#pairs.length > 1;
    const hasNestedSplitters = this.querySelectorAll('ids-splitter').length > 0;
    const shouldApplyMinDistance = hasMultipleSplitBars || hasNestedSplitters;

    const minDistancePercent = shouldApplyMinDistance ? this.#toPercentage(this.minDistance) : 0;
    const startMinConstraint = Math.max(start.minSize, minDistancePercent);
    const endMinConstraint = Math.max(end.minSize, minDistancePercent);

    // Work with the diff directly - it's already in logical space (+ = grow start pane)
    let newDiff = diff;

    // Apply constraints - use newDiff consistently for both RTL and LTR
    if (newDiff < 0 && newDiff < ((startSize + startMinConstraint) + pad + extra.min)) {
      // Moving left/up: constrain by start pane minimum
      newDiff = startSize + startMinConstraint + extra.min;
    } else if (newDiff > 0 && newDiff > ((useEndSize - endMinConstraint) - pad - extra.max)) {
      // Moving right/down: constrain by end pane minimum
      newDiff = useEndSize - endMinConstraint - extra.max;
    }

    return newDiff === Infinity ? 0 : newDiff;
  }

  /**
   * Update pair size with given difference percentages value.
   * @private
   * @param {object} pair The pair with diff already calculated
   * @returns {object} new updated size
   */
  updateSize(pair: any): object {
    this.initialized = false;
    const { start, end, diff } = pair;
    const newSize: any = { diff };

    const targetSize = start.size + newSize.diff;
    const endTargetSize = end.size - newSize.diff;

    // Use the pair's sizes for constraint redistribution (consistent with diff calculation)
    const totalSize = start.size + end.size;

    if (pair.initial) {
      newSize.start = Math.max(targetSize, 0);
      newSize.end = Math.max(endTargetSize, 0);
    } else {
      const minDistancePercent = this.#toPercentage(this.minDistance);
      const isCollapsing = targetSize <= 0 || endTargetSize <= 0;

      newSize.start = isCollapsing ? Math.max(targetSize, 0) : Math.max(targetSize, minDistancePercent);
      newSize.end = isCollapsing ? Math.max(endTargetSize, 0) : Math.max(endTargetSize, minDistancePercent);
    }

    // Enforce max-size constraints on both panes simultaneously
    const startMaxAttr = start.pane.getAttribute(attributes.MAX_SIZE);
    const endMaxAttr = end.pane.getAttribute(attributes.MAX_SIZE);
    const startMax = startMaxAttr ? this.#percentageVal(startMaxAttr) : Infinity;
    const endMax = endMaxAttr ? this.#percentageVal(endMaxAttr) : Infinity;

    // Enforce min-size constraints from pane attributes
    const startMinAttr = start.pane.getAttribute(attributes.MIN_SIZE);
    const endMinAttr = end.pane.getAttribute(attributes.MIN_SIZE);
    const startMin = startMinAttr ? this.#percentageVal(startMinAttr) : 0;
    const endMin = endMinAttr ? this.#percentageVal(endMinAttr) : 0;

    // Clamp to min-size first
    if (newSize.start < startMin && startMin > 0) {
      newSize.end += (newSize.start - startMin);
      newSize.start = startMin;
    }
    if (newSize.end < endMin && endMin > 0) {
      newSize.start += (newSize.end - endMin);
      newSize.end = endMin;
    }

    if (newSize.start > startMax) {
      newSize.start = startMax;
      newSize.end = totalSize - startMax;
    }
    if (newSize.end > endMax) {
      newSize.end = endMax;
      newSize.start = totalSize - endMax;
    }

    // Final clamp: ensure neither pane violates constraints after redistribution
    newSize.start = Math.max(Math.min(newSize.start, startMax), startMin);
    newSize.end = Math.max(Math.min(newSize.end, endMax), endMin);

    start.pane.style[this.#prop.dimension] = start.pane.isPixelBased() ? `${this.#round(this.#toPixel(newSize.start))}px` : `${this.#round(newSize.start)}%`;
    end.pane.style[this.#prop.dimension] = end.pane.isPixelBased() ? `${this.#round(this.#toPixel(newSize.end))}px` : `${this.#round(newSize.end)}%`;

    // Set attributes without triggering the pane's size setter cascade
    const wasRefreshing = this.#isRefreshing;
    this.#isRefreshing = true;
    if (start.pane.isPixelBased()) {
      const startPx = this.#round(this.#toPixel(newSize.start));
      start.pane.setAttribute(attributes.SIZE, `${startPx}px`);
      start.pane.state.pixelSizeValue = startPx;
    } else {
      start.pane.setAttribute(attributes.SIZE, `${this.#round(newSize.start)}%`);
    }
    if (end.pane.isPixelBased()) {
      const endPx = this.#round(this.#toPixel(newSize.end));
      end.pane.setAttribute(attributes.SIZE, `${endPx}px`);
      end.pane.state.pixelSizeValue = endPx;
    } else {
      end.pane.setAttribute(attributes.SIZE, `${this.#round(newSize.end)}%`);
    }
    this.#isRefreshing = wasRefreshing;

    start.pane.toggleAttribute(COLLAPSED, !this.disableAutoCollapse && newSize.start === 0);
    end.pane.toggleAttribute(COLLAPSED, !this.disableAutoCollapse && newSize.end === 0);

    this.initialized = true;
    return newSize;
  }

  /**
   * Trigger given event with current params.
   * @private
   * @param {string} evt The event to be trigger.
   * @param {object} pair The pair.
   * @param {object} res The veto response method.
   * @returns {void}
   */
  #triggerEvent(evt: string, pair: any, res?: any): void {
    const { start, end, splitBar } = pair;
    const args = {
      detail: {
        elem: this,
        start: {
          ...start,
          size: start.pane.isPixelBased() ? start.pane.state.pixelSizeValue : start.size,
          unit: start.pane.isPixelBased() ? 'px' : '%'
        },
        end,
        splitBar,
        sizes: this.sizes(),
        minSizes: this.minSizes(),
        maxSizes: this.maxSizes()
      }
    };
    if (typeof res === 'function') {
      (args.detail as any).response = res;
    }
    this.triggerEvent(evt, this, args);
  }

  /**
   * Update given pair size and adjust split bars min/max positions.
   * @private
   * @param {object} pair The pair.
   * @returns {void}
   */
  #updateSizeAndSplitBar(pair: any): void {
    const { start, end, idx, diff } = pair; // eslint-disable-line

    // Update and get new updated size
    const newSize: any = this.updateSize({ start, end, diff });
    this.#setPaneSize(start.idx, newSize.start);
    this.#setPaneSize(end.idx, newSize.end);
    start.size = newSize.start;
    end.size = newSize.end;

    // Adjust min/max on split bar, if more than one split bars
    const { minTransform, maxTransform, useRTL } = this.#prop; // eslint-disable-line
    const last = this.#pairs.length - 1;
    if (last > 0) {
      const pixelDiff = this.#toPixel(newSize.diff);
      const sb = {
        prev: this.#pairs[idx - 1]?.splitBar,
        next: this.#pairs[idx + 1]?.splitBar
      } as any;
      if (idx === 0) {
        if (useRTL) sb.next[maxTransform] -= pixelDiff;
        else sb.next[minTransform] += pixelDiff;
      } else if (idx === last) {
        if (useRTL) sb.prev[minTransform] -= pixelDiff;
        else sb.prev[maxTransform] += pixelDiff;
      } else {
        if (useRTL) sb.prev[minTransform] -= pixelDiff;
        else sb.prev[maxTransform] += pixelDiff;
        if (useRTL) sb.next[maxTransform] -= pixelDiff;
        else sb.next[minTransform] += pixelDiff;
      }
    }
  }

  /**
   * Get given value as percentages
   * @private
   * @param {string|number|null} value The value
   * @returns {number} The percentage value
   */
  #percentageVal(value: string | number | null): number {
    return /%$/i.test(`${value}`)
      ? parseFloat(`${value}`) : this.#toPercentage(value);
  }

  /**
   * Round a number to 4 decimal places to avoid long floating-point strings
   * in percentage and pixel attributes/CSS values (e.g. 83.33333333333334% → 83.3333%).
   * @private
   * @param {number} value The value to round
   * @returns {number} The rounded value
   */
  #round(value: number): number {
    return Math.round(value * 10000) / 10000;
  }

  /**
   * Convert given value to percentages
   * @private
   * @param {string|number|null} value The value
   * @returns {number} The percentage value
   */
  #toPercentage(value: string | number | null): number {
    if (value === null) return 0;

    // Handle numeric values - treat as pixels, convert to percentage
    if (typeof value === 'number') {
      if (this.#container.size === 0) return 0;
      return (value / this.#container.size) * 100;
    }

    // Handle string values
    const stringValue = `${value}`;
    const numericValue = parseFloat(stringValue);

    if (Number.isNaN(numericValue)) return 0;

    // If already a percentage, return as-is
    if (/%$/i.test(stringValue)) {
      return numericValue;
    }

    // If pixels, convert to percentage
    if (/px$/i.test(stringValue)) {
      if (this.#container.size === 0) return 0;
      return (numericValue / this.#container.size) * 100;
    }

    // Bare numeric string (no unit) - treat as pixels
    if (/^\d+(\.\d+)?$/.test(stringValue.trim())) {
      if (this.#container.size === 0) return 0;
      return (numericValue / this.#container.size) * 100;
    }

    return 0;
  }

  /**
   * Convert given value to pixels
   * @private
   * @param {string|number|null} value The value
   * @returns {number} The pixels value
   */
  #toPixel(value: string | number | null): number {
    if (value === null) return 0;

    // Handle numeric values - treat as percentage, convert to pixels
    if (typeof value === 'number') {
      if (this.#container.size === 0) return 0;
      return (this.#container.size * value) / 100;
    }

    // Handle string values
    const stringValue = `${value}`;
    const numericValue = parseFloat(stringValue);

    if (Number.isNaN(numericValue)) return 0;

    // If percentage, convert to pixels
    if (/%$/i.test(stringValue)) {
      if (this.#container.size === 0) return 0;
      return (this.#container.size * numericValue) / 100;
    }

    // If already pixels, return as-is
    if (/px$/i.test(stringValue)) {
      return numericValue;
    }

    // Bare numeric string (no unit) - treat as pixels
    if (/^\d+(\.\d+)?$/.test(stringValue.trim())) {
      return numericValue;
    }

    return 0;
  }

  /**
   * Generate unique pane id.
   * @private
   * @returns {string} The genrated id.
   */
  #uniquePaneId(): string {
    return `splitter-pane-${Math.floor(Math.random() * Date.now())}`;
  }

  /**
   * Add expand button to split bar
   * @private
   * @param {number} index The split bar index
   * @returns {void}
   */
  #addExpandButton(index: number): void {
    const splitBar = this.container?.querySelector(`#${this.#splitId(index)}`);
    if (!splitBar) return;

    // Remove existing button
    splitBar.querySelector('.splitter-expand-btn')?.remove();

    const expandBtn = document.createElement('div');
    expandBtn.className = 'splitter-expand-btn';
    expandBtn.setAttribute(htmlAttributes.ROLE, attributes.BUTTON);
    expandBtn.setAttribute(attributes.TABINDEX, '0');
    expandBtn.setAttribute(htmlAttributes.ARIA_LABEL, 'Expand panel');

    // Add click and keyboard event handlers
    const handleToggle = (e: Event) => {
      if (e.type === 'keydown') {
        const keyEvent = e as KeyboardEvent;
        if (keyEvent.key !== 'Enter' && keyEvent.key !== ' ') return;
      }
      e.preventDefault();
      this.#toggleCollapsed(index);
    };

    // Store handler reference for cleanup
    (expandBtn as any).handleToggle = handleToggle;

    expandBtn.addEventListener('click', handleToggle);
    expandBtn.addEventListener('touchstart', handleToggle);
    expandBtn.addEventListener('keydown', handleToggle);

    // Initialize button state
    const pair = this.#pairs[index - 1];
    if (pair) {
      const currentSizePixels = this.#toPixel(this.#getPaneSize(pair.start.idx));
      const collapseThreshold = this.minWidth > 0 ? this.minWidth : IdsSplitter.COLLAPSED_THRESHOLD_PX;
      const isCurrentlyCollapsed = currentSizePixels <= collapseThreshold;
      expandBtn.setAttribute('data-collapsed', isCurrentlyCollapsed ? 'true' : 'false');
      expandBtn.classList.toggle('is-collapsed', isCurrentlyCollapsed);
    }

    splitBar.appendChild(expandBtn);
  }

  /**
   * Minimum size threshold in pixels to consider a pane as collapsed
   * @private
   */
  static readonly COLLAPSED_THRESHOLD_PX = 40;

  /**
   * Toggle the collapsed state of the splitter
   * @private
   * @param {number} index The split bar index
   * @returns {void}
   */
  #toggleCollapsed(index: number): void {
    const pair = this.#pairs[index - 1];
    if (!pair) return;

    // Set flag to prevent dragend handler from overwriting expand sizes
    this.#isTogglingExpand = true;

    // Ensure container dimensions are up to date before calculations
    this.#setContainer();

    const { start, end } = pair;
    const collapseThreshold = this.minWidth > 0 ? this.minWidth : IdsSplitter.COLLAPSED_THRESHOLD_PX;

    // Check if panes are collapsed - use percentage-based threshold as fallback
    // when container.size is 0 or unavailable
    const startSizePercent = this.#getPaneSize(start.idx);
    const endSizePercent = this.#getPaneSize(end.idx);
    let startCollapsed = false;
    let endCollapsed = false;

    if (this.#container.size > 0) {
      // If container size is valid, use pixel-based threshold
      const startSizePixels = Math.floor(this.#toPixel(startSizePercent));
      const endSizePixels = Math.floor(this.#toPixel(endSizePercent));
      startCollapsed = startSizePixels <= collapseThreshold;
      endCollapsed = endSizePixels <= collapseThreshold;
    } else {
      // Fallback: use percentage-based threshold (estimate ~10% as collapsed)
      const collapsedPercentageThreshold = 10;
      startCollapsed = startSizePercent <= collapsedPercentageThreshold;
      endCollapsed = endSizePercent <= collapsedPercentageThreshold;
    }

    // Check if either pane has a saved expand size that differs significantly from current size
    // Compare percentages directly instead of pixels to avoid container.size = 0 issues
    const startExpandSize = start.pane.getExpandSize();
    const endExpandSize = end.pane.getExpandSize();
    const startCurrentSize = this.#getPaneSize(start.idx);
    const endCurrentSize = this.#getPaneSize(end.idx);

    // If expand size differs by more than 10% points, we should expand to it
    const sizeDiffThreshold = 10; // percentage points
    const startHasDifferentExpandSize = startExpandSize > 0
      && Math.abs(startExpandSize - startCurrentSize) > sizeDiffThreshold;
    const endHasDifferentExpandSize = endExpandSize > 0
      && Math.abs(endExpandSize - endCurrentSize) > sizeDiffThreshold;

    let targetPane;
    let isCurrentlyCollapsed;
    let shouldExpandToSavedSize = false;

    if (startCollapsed) {
      // Start pane is physically collapsed, expand it
      targetPane = start;
      isCurrentlyCollapsed = true;
    } else if (endCollapsed) {
      // End pane is physically collapsed, expand it
      targetPane = end;
      isCurrentlyCollapsed = true;
    } else if (startHasDifferentExpandSize) {
      // Start pane is not collapsed but has a different saved expand size
      // This means it was previously at a custom position and we should return to it
      targetPane = start;
      isCurrentlyCollapsed = false;
      shouldExpandToSavedSize = true;
    } else if (endHasDifferentExpandSize) {
      // End pane has a different saved expand size
      targetPane = end;
      isCurrentlyCollapsed = false;
      shouldExpandToSavedSize = true;
    } else {
      // Neither pane is collapsed and neither has a custom expand size
      // Default to collapsing the start pane
      targetPane = start;
      isCurrentlyCollapsed = false;
    }

    // Calculate target size and diff for the operation
    let targetSizePercent: number;
    const currentSize = this.#getPaneSize(targetPane.idx);

    if (isCurrentlyCollapsed || shouldExpandToSavedSize) {
      // Expanding: use saved expand size or default size
      targetSizePercent = targetPane.pane.getExpandSize() || this.#defaultsSize;
    } else {
      // Collapsing: save current size if we don't have one already
      if ((!targetPane.pane.getExpandSize() || targetPane.pane.getExpandSize() === 0)
        && currentSize > this.#toPercentage(collapseThreshold)) {
        targetPane.pane.saveExpandSize(currentSize);
      }
      // Collapse to threshold percentage
      targetSizePercent = this.#toPercentage(collapseThreshold);
    }

    // Calculate the difference between current and target size
    const diff = targetSizePercent - currentSize;

    // Create a modified pair with the target pane as start
    const modifiedPair = targetPane === start ? pair : { ...pair, start: end, end: start };

    // Use the existing #move() mechanism to handle the resize
    this.#move({ ...modifiedPair, diff: this.#toPixel(diff) });

    // Reposition all split bars based on new pane sizes
    // This is critical - #move() only updates one split bar's transform,
    // but collapse/expand changes pane sizes so ALL split bars need repositioning
    this.#positionSplitBars();

    // Update collapsed attribute explicitly (don't rely on #setCollapsedAttribute's async logic)
    const isCollapsing = targetSizePercent <= this.#toPercentage(collapseThreshold);
    if (isCollapsing && !this.disableAutoCollapse) {
      targetPane.pane.setAttribute(COLLAPSED, '');
    } else {
      targetPane.pane.removeAttribute(COLLAPSED);
    }

    // Update expand button state
    this.#updateExpandButtonState(pair.idx + 1, isCollapsing);

    // Clear flag immediately - #move() is synchronous and doesn't trigger drag events
    this.#isTogglingExpand = false;
  }

  /**
   * Update expand button state based on collapsed status
   * @private
   * @param {number} index The split bar index
   * @param {boolean} isCollapsed Whether the pane is collapsed
   * @returns {void}
   */
  #updateExpandButtonState(index: number, isCollapsed: boolean): void {
    const splitBar = this.container?.querySelector(`#${this.#splitId(index)}`);
    const expandBtn = splitBar?.querySelector('.splitter-expand-btn') as HTMLElement;

    if (expandBtn) {
      expandBtn.classList.toggle('is-collapsed', isCollapsed);
      expandBtn.setAttribute(htmlAttributes.ARIA_LABEL, `${isCollapsed ? 'Expand' : 'Collapse'} panel`);
      expandBtn.setAttribute('data-collapsed', isCollapsed ? 'true' : 'false');

      // Always show button when collapsed, hide when expanded
      if (isCollapsed) {
        expandBtn.style.opacity = '1';
        expandBtn.style.visibility = 'visible';
      } else {
        expandBtn.style.opacity = '';
        expandBtn.style.visibility = '';
      }
    }

    // Set RTL class for CSS targeting
    if (this.localeAPI?.isRTL()) {
      this.classList.add('rtl');
    } else {
      this.classList.remove('rtl');
    }
  }

  /**
   * Establish Internal Event Handlers
   * @private
   * @returns {object} This API object for chaining
   */
  #attachEventHandlers(): object {
    const slot = this.shadowRoot?.querySelector('slot');
    this.offEvent('slotchange.splitter', slot);
    this.onEvent('slotchange.splitter', slot, () => {
      this.#init();
    });

    // Draggable events
    this.#pairs.forEach((pair, i) => {
      const sb = pair.splitBar;
      const namespace = `splitter${i}`;

      this.offEvent(`dragstart.${namespace}`, sb);
      this.onEvent(`dragstart.${namespace}`, sb, () => {
        if (!this.disabled) {
          const dragContext = this.#createDragContext(pair);
          if (dragContext) {
            this.#dragContexts.set(sb, dragContext);

            // Update this bar's transform limits based on current adjacent bar positions
            // and max-size constraints so ids-draggable enforces them during drag
            this.#updateDragLimits(i, sb, pair);
          }
        }
      });

      this.offEvent(`drag.${namespace}`, sb);
      this.onEvent(`drag.${namespace}`, sb, (e: CustomEvent) => {
        this.#moveDragHandle(e.detail);
        if (!this.disabled && !this.resizeOnDragEnd) {
          const dragContext = this.#dragContexts.get(sb);
          if (dragContext) {
            // Get physical pixel delta from drag event
            let pixelDiff = e.detail[this.#prop.delta];
            // In RTL, flip the physical delta to logical space
            // Physical right (+) should shrink start pane (-) in RTL
            if (this.#prop.useRTL) pixelDiff *= -1;
            const diff = this.#adjustDiff(
              { ...pair, diff: this.#toPercentage(pixelDiff) },
              dragContext
            );
            this.updateSize({ ...pair, diff });
          }
        }
      });

      this.offEvent(`dragend.${namespace}`, sb);
      this.onEvent(`dragend.${namespace}`, sb, (e: CustomEvent) => {
        if (!this.disabled) {
          const dragContext = this.#dragContexts.get(sb);
          if (dragContext) {
            // Get physical pixel delta from drag event
            let pixelDiff = e.detail[this.#prop.delta];
            // In RTL, flip the physical delta to logical space
            if (this.#prop.useRTL) pixelDiff *= -1;
            const diff = this.#adjustDiff(
              { ...pair, diff: this.#toPercentage(pixelDiff) },
              dragContext
            );
            this.#completeDrag({ ...pair, diff });

            // Only save expand sizes if this is a real user drag, not a programmatic toggle
            if (!this.#isTogglingExpand) {
              // Save the size after drag completes (user's intended position)
              // This ensures expand button remembers where user dragged to
              const { start, end } = pair;
              const collapseThreshold = this.minWidth > 0 ? this.minWidth : IdsSplitter.COLLAPSED_THRESHOLD_PX;
              const startSizePixels = this.#toPixel(this.#getPaneSize(start.idx));
              const endSizePixels = this.#toPixel(this.#getPaneSize(end.idx));

              // Save expand size for the start pane if it's not collapsed
              if (startSizePixels > (collapseThreshold + 1)) {
                start.pane.saveExpandSize(this.#getPaneSize(start.idx));
              }
              // Save expand size for the end pane if it's not collapsed
              if (endSizePixels > (collapseThreshold + 1)) {
                end.pane.saveExpandSize(this.#getPaneSize(end.idx));
              }
            }

            this.#dragContexts.delete(sb);
          }
        }
      });

      this.offEvent(`keydown.${namespace}`, sb);
      this.onEvent(`keydown.${namespace}`, sb, (e: KeyboardEvent) => {
        if (!this.disabled) {
          // Keep `Space` in keydown allow options, so page not scrolls
          const allow = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Space'];
          const key = e.code;
          if (allow.indexOf(key) > -1) {
            if (key !== 'Space') {
              // Keyboard movement is in physical space (visual direction)
              // We need to convert to logical space (+ = grow start pane)
              let move = 10;

              if (key === 'ArrowDown' || key === 'ArrowLeft') {
                // Physical: Down (vertical) or Left (horizontal)
                if (!this.isHorizontal) {
                  move *= 1; // Down = grow start pane (top pane)
                } else {
                  // Left: shrink in both LTR and RTL (moving split bar left always shrinks left side visually)
                  move *= -1;
                }
              } else if (key === 'ArrowUp' || key === 'ArrowRight') {
                // Physical: Up (vertical) or Right (horizontal)
                if (!this.isHorizontal) {
                  move *= -1; // Up = shrink start pane (top pane)
                } else {
                  // Right: grow in both LTR and RTL (moving split bar right always grows left side visually)
                  move *= 1;
                }
              }

              this.#move({ ...pair, diff: move });
            }
            e.preventDefault();
            e.stopPropagation();
          }
        }
      });

      this.offEvent(`touchstart.${namespace}`, sb);
      this.onEvent(`touchstart.${namespace}`, sb, (e: TouchEvent) => {
        if (!this.disabled) {
          if (!sb?.classList.contains('touch-active')) {
            sb?.classList.add('touch-active');
            setTimeout(() => sb?.classList.remove('touch-active'), 3000);
          }
          // Position drag handle to touch location
          if (e.touches[0]) {
            this.#moveDragHandle({ clientX: e.touches[0].clientX, clientY: e.touches[0].clientY } as MouseEvent);
          }
        }
      });
    });

    // Set observer for resize
    this.#resizeObserver.disconnect();
    if (this.container) this.#resizeObserver.observe(this.container);

    // Observe parent element for size changes caused by sibling visibility changes
    this.#parentResizeObserver.disconnect();
    if (this.parentElement) this.#parentResizeObserver.observe(this.parentElement);

    // Observe previous sibling (e.g. ids-header) for size changes
    this.#siblingResizeObserver.disconnect();
    this.#siblingMutationObserver?.disconnect();
    const prevSibling = this.previousElementSibling;
    if (prevSibling) {
      this.#siblingResizeObserver.observe(prevSibling);
      this.#siblingMutationObserver = new MutationObserver(() => this.resize());
      this.#siblingMutationObserver.observe(prevSibling, { attributes: true, attributeFilter: ['style', 'hidden'] });
    }

    // Set events to move handle
    this.onEvent('mouseenter.splitter', this.container, (e: MouseEvent) => {
      this.#moveDragHandle(e);
    });
    this.onEvent('mousemove.splitter', this.container, (e: MouseEvent) => {
      this.#moveDragHandle(e);
    });
    this.onEvent('themechanged.splitter', document, () => {
      this.#resizeObserver.disconnect();
      requestAnimationTimeout(() => {
        if (this.container) this.#resizeObserver.observe(this.container);
      }, 350);
    });
    return this;
  }

  /** Handle Languages Changes - for switching between RTL to LTR */
  onLanguageChange = () => {
    this.resize();
  };

  /**
   * Destroy added elements and unbind events.
   * @returns {object} This API object for chaining
   */
  #destroy(): object {
    const slot = this.shadowRoot?.querySelector('slot');
    this.offEvent('slotchange.splitter', slot);
    this.#resizeObserver.disconnect();
    this.#parentResizeObserver.disconnect();
    this.#siblingResizeObserver.disconnect();
    this.#siblingMutationObserver?.disconnect();
    this.#initObserver?.disconnect();

    this.#pairs.forEach((pair, i) => {
      const { start, end, splitBar } = pair;
      const namespace = `splitter${i}`;
      const expandBtn = splitBar?.querySelector('.splitter-expand-btn') as any;
      if (expandBtn?.handleToggle) {
        expandBtn.removeEventListener('click', expandBtn.handleToggle);
        expandBtn.removeEventListener('touchstart', expandBtn.handleToggle);
        expandBtn.removeEventListener('keydown', expandBtn.handleToggle);
      }
      start.pane.removeAttribute(attributes.STYLE);
      end.pane.removeAttribute(attributes.STYLE);
      this.offEvent(`dragstart.${namespace}`, splitBar);
      this.offEvent(`drag.${namespace}`, splitBar);
      this.offEvent(`dragend.${namespace}`, splitBar);
      this.offEvent(`click.${namespace}`, splitBar);
      this.offEvent(`touchstart.${namespace}`, splitBar);
      this.offEvent(`keydown.${namespace}`, splitBar);
      splitBar?.remove?.();
    });

    this.#container = { size: 0 };
    this.#prop = {};
    this.#pairs = [];
    this.#defaultsSize = 0;

    return this;
  }

  /**
   * Check if current orientation is horizontal
   * @returns {boolean} True if, orientation is horizontal
   */
  get isHorizontal(): boolean { return this.#prop.orientation === 'horizontal'; }

  /**
   * Set the splitter axis direction x: horizontal or y: vertical
   * @param {string} value of the axis x or y
   */
  set axis(value: string) {
    if (value !== this.state.axis) {
      const prefixed = (v: string) => `axis-${v}`;
      this.container?.classList.remove(...AXIS.map((v) => prefixed(v)));
      if (AXIS.indexOf(value) > -1) {
        this.setAttribute(attributes.AXIS, value);
        this.state.axis = value;
      } else {
        this.removeAttribute(attributes.AXIS);
        this.state.axis = SPLITTER_DEFAULTS.axis;
      }
      this.container?.classList.add(prefixed(this.state.axis));
      this.#init();
    }
  }

  get axis(): string { return this.state.axis || 'x'; }

  /**
   * Sets the splitter to disabled
   * @param {boolean | string} value If true will set disabled attribute
   */
  set disabled(value: boolean | string) {
    const val = stringToBool(value);
    if (val !== this.state.disabled) {
      this.state.disabled = val;
      if (val) {
        this.setAttribute(attributes.DISABLED, '');
      } else {
        this.removeAttribute(attributes.DISABLED);
      }
      this.#setDisabled();
    }
  }

  get disabled(): boolean | string { return this.state.disabled; }

  /**
   * Sets the splitter to disable auto collapse on panes
   * @param {boolean | string} value If true, the collapsed attribute will not be set on panes
   */
  set disableAutoCollapse(value: boolean | string) {
    const val = stringToBool(value);
    if (val !== this.state.disableAutoCollapse) {
      this.state.disableAutoCollapse = val;
      this.toggleAttribute(attributes.DISABLE_AUTO_COLLAPSE, val);
    }
  }

  get disableAutoCollapse(): boolean { return this.state.disableAutoCollapse; }

  /**
   * Set the aria-label text for each split bar.
   * @param {string} value of the label text.
   */
  set label(value: string) {
    if (value !== this.state.label) {
      if (value) {
        this.setAttribute(attributes.LABEL, value);
        this.state.label = value;
      } else {
        this.removeAttribute(attributes.LABEL);
        this.state.label = SPLITTER_DEFAULTS.label;
      }
    }
  }

  get label(): string { return this.state.label; }

  /**
   * Set the minimum distance for split bars.
   * @param {number|string} value The minimum distance in pixels.
   */
  set minDistance(value: number | string) {
    const val = parseInt(`${value}`, 10);

    if (!Number.isNaN(val) && val >= 0) {
      if (val !== this.state.minDistance) {
        this.setAttribute(attributes.MIN_DISTANCE, `${val}`);
        this.state.minDistance = val;
      }
    } else {
      this.removeAttribute(attributes.MIN_DISTANCE);
      this.state.minDistance = 0;
    }
  }

  get minDistance(): number {
    return this.state.minDistance === null || this.state.minDistance === undefined ? 16 : this.state.minDistance;
  }

  /**
   * Sets the splitter to resize on drag end
   * @param {boolean | string} value If true will set to resize on drag end
   */
  set resizeOnDragEnd(value: boolean | string) {
    const val = stringToBool(value);
    if (val !== this.state.resizeOnDragEnd) {
      this.state.resizeOnDragEnd = val;
      if (val) {
        this.setAttribute(attributes.RESIZE_ON_DRAG_END, '');
      } else {
        this.removeAttribute(attributes.RESIZE_ON_DRAG_END);
      }
    }
  }

  get resizeOnDragEnd(): boolean | string { return this.state.resizeOnDragEnd; }

  /**
   *  Set splitter save position to local storage.
   * @param {boolean | string} value if true, will allow to save position to local storage.
   */
  set savePosition(value: boolean | string) {
    const val = stringToBool(value);
    if (val !== this.state.savePosition) {
      this.state.savePosition = val;
      if (val) {
        this.setAttribute(attributes.SAVE_POSITION, '');
      } else {
        this.removeAttribute(attributes.SAVE_POSITION);
      }
    }
  }

  get savePosition(): boolean | string { return this.state.savePosition; }

  /**
   * Set uniqueId to save to local storage.
   * @param {number|string|null} value A uniqueId use to save to local storage.
   */
  set uniqueId(value: number | string | null) {
    const val = /number|string/g.test(typeof value) ? `${value}` : null;
    if (typeof val === 'string' && val !== '') {
      if (val !== this.state.uniqueId) {
        this.setAttribute(attributes.UNIQUE_ID, val);
        this.state.uniqueId = val;
      }
      return;
    }
    this.removeAttribute(attributes.UNIQUE_ID);
    this.state.uniqueId = SPLITTER_DEFAULTS.uniqueId;
  }

  get uniqueId(): string | null { return this.state.uniqueId; }

  /**
   * Set whether to show expand button
   * @param {boolean | string} value If true will show expand button
   */
  set showExpandButton(value: boolean | string) {
    const val = stringToBool(value);
    if (val !== this.state.showExpandButton) {
      this.state.showExpandButton = val;
      if (val) {
        this.setAttribute(attributes.SHOW_EXPAND_BUTTON, '');
      } else {
        this.removeAttribute(attributes.SHOW_EXPAND_BUTTON);
      }
      // Re-initialize to update button visibility
      if (this.initialized) {
        this.#init();
      }
    }
  }

  get showExpandButton(): boolean | string {
    // Check attribute first, then state, then default
    if (this.hasAttribute(attributes.SHOW_EXPAND_BUTTON)) {
      return stringToBool(this.getAttribute(attributes.SHOW_EXPAND_BUTTON));
    }
    return this.state.showExpandButton ?? SPLITTER_DEFAULTS.showExpandButton;
  }

  /**
   * Set to true to wrap text
   * @param {boolean} value If true will show expand button
   */
  set textWrap(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.TEXT_WRAP, '');
    } else {
      this.removeAttribute(attributes.TEXT_WRAP);
    }
  }

  get textWrap(): boolean {
    return stringToBool(this.getAttribute(attributes.TEXT_WRAP));
  }

  /**
   * Set expanded width for expand behavior
   * @param {number|string} value The expanded width in pixels
   */
  set expandedWidth(value: number | string) {
    const val = parseInt(`${value}`, 10) || SPLITTER_DEFAULTS.expandedWidth;
    if (val !== this.state.expandedWidth) {
      this.state.expandedWidth = val;
      this.setAttribute(attributes.EXPANDED_WIDTH, `${val}`);
    }
  }

  get expandedWidth(): number { return this.state.expandedWidth ?? SPLITTER_DEFAULTS.expandedWidth; }

  /**
   * Set minimum width for dragging constraint
   * @param {number|string} value The minimum width in pixels
   */
  set minWidth(value: number | string) {
    const val = parseInt(`${value}`, 10) || SPLITTER_DEFAULTS.minWidth;
    if (val !== this.state.minWidth) {
      this.state.minWidth = val;
      this.setAttribute(attributes.MIN_WIDTH, `${val}`);
    }
  }

  get minWidth(): number { return this.state.minWidth ?? SPLITTER_DEFAULTS.minWidth; }
}
